// Enhanced Translation Service using backend API
class TranslationService {
  constructor() {
    this.cache = new Map();
    this.persistentCache = this.loadPersistentCache();
    this.supportedLanguages = ['en', 'nl'];
    this.baseURL = process.env.REACT_APP_API_URL || '';
    
    // Define which content should NOT be auto-translated (manual translations only)
    this.manualTranslationKeys = [
      'nav.',
      'footer.',
      'common.readMore',
      'common.learnMore',
      'common.getStarted',
      'common.viewAll',
      'common.getInTouch',
      'common.findUs',
      'common.requestQuote',
      'common.contactUs',
      'common.submit',
      'common.send',
      'common.save',
      'common.cancel',
      'common.close',
      'common.back',
      'common.next',
      'common.previous',
      'common.loading',
      'common.error',
      'common.success'
    ];
    
    this.rateLimitDelay = 500; // 0.5 second between requests
    this.lastRequestTime = 0;
  }

  // Load persistent cache from localStorage
  loadPersistentCache() {
    try {
      const cached = localStorage.getItem('translation-cache');
      return cached ? new Map(JSON.parse(cached)) : new Map();
    } catch (error) {
      console.warn('Failed to load translation cache:', error);
      return new Map();
    }
  }

  // Save cache to localStorage
  savePersistentCache() {
    try {
      const cacheArray = Array.from(this.persistentCache.entries());
      localStorage.setItem('translation-cache', JSON.stringify(cacheArray));
    } catch (error) {
      console.warn('Failed to save translation cache:', error);
    }
  }

  // Check if a translation key should use manual translation only
  isManualTranslationKey(key) {
    return this.manualTranslationKeys.some(pattern => key.startsWith(pattern));
  }

  // Generate cache key
  getCacheKey(text, from, to) {
    return `${from}-${to}-${text}`;
  }

  // Clean URL-encoded text comprehensively
  cleanText(text) {
    if (!text || typeof text !== 'string') return text;
    
    let cleaned = text;
    
    // Multiple rounds of URL decoding to handle nested encoding
    for (let i = 0; i < 3; i++) {
      try {
        const decoded = decodeURIComponent(cleaned);
        if (decoded === cleaned) break; // No more decoding needed
        cleaned = decoded;
      } catch (error) {
        break; // Stop if decoding fails
      }
    }
    
    // Manual replacement of common URL-encoded characters that might remain
    cleaned = cleaned
      .replace(/%2C/g, ',')
      .replace(/%2F/g, '/')
      .replace(/%3A/g, ':')
      .replace(/%3F/g, '?')
      .replace(/%21/g, '!')
      .replace(/%40/g, '@')
      .replace(/%23/g, '#')
      .replace(/%24/g, '$')
      .replace(/%25/g, '%')
      .replace(/%5E/g, '^')
      .replace(/%26/g, '&')
      .replace(/%2A/g, '*')
      .replace(/%28/g, '(')
      .replace(/%29/g, ')')
      .replace(/%2B/g, '+')
      .replace(/%3D/g, '=')
      .replace(/%5B/g, '[')
      .replace(/%5D/g, ']')
      .replace(/%7B/g, '{')
      .replace(/%7D/g, '}')
      .replace(/%7C/g, '|')
      .replace(/%5C/g, '\\')
      .replace(/%3B/g, ';')
      .replace(/%22/g, '"')
      .replace(/%27/g, "'")
      .replace(/%3C/g, '<')
      .replace(/%3E/g, '>')
      .replace(/%20/g, ' ');
    
    return cleaned.trim();
  }

  // Translate text using backend API with caching
  async translateText(text, fromLang = 'en', toLang = 'nl', options = {}) {
    if (!text || text.trim() === '') return text;
    if (fromLang === toLang) return text;
    
    // Clean any URL encoding from the text
    text = this.cleanText(text);
    
    // Check if target language is supported
    if (!this.supportedLanguages.includes(toLang)) {
      console.warn(`Language ${toLang} not supported, falling back to English`);
      return text;
    }

    const cacheKey = this.getCacheKey(text, fromLang, toLang);
    
    // Check persistent cache first
    if (this.persistentCache.has(cacheKey)) {
      const cached = this.persistentCache.get(cacheKey);
      this.cache.set(cacheKey, cached); // Also store in memory cache
      return cached;
    }
    
    // Check memory cache
    if (this.cache.has(cacheKey)) {
      return this.cache.get(cacheKey);
    }

    // Skip translation for very short text or numbers
    if (text.length < 2 || /^\d+$/.test(text.trim())) {
      return text;
    }

    // Rate limiting
    const now = Date.now();
    const timeSinceLastRequest = now - this.lastRequestTime;
    if (timeSinceLastRequest < this.rateLimitDelay) {
      await new Promise(resolve => setTimeout(resolve, this.rateLimitDelay - timeSinceLastRequest));
    }
    this.lastRequestTime = Date.now();

    try {
      const response = await fetch(`${this.baseURL}/api/translation/translate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          text: text,
          from: fromLang,
          to: toLang
        })
      });

      if (!response.ok) {
        throw new Error(`Translation API returned ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      let translatedText = data.translatedText;

      if (translatedText && translatedText !== text && translatedText.trim() !== '') {
        // Clean any URL-encoded characters from the translated result
        translatedText = this.cleanText(translatedText);
        
        // Cache the result in both memory and persistent storage
        this.cache.set(cacheKey, translatedText);
        this.persistentCache.set(cacheKey, translatedText);
        this.savePersistentCache();
        
        if (!options.silent) {
          console.log(`✅ Translated via backend API: "${text}" → "${translatedText}"`);
        }
        return translatedText;
      }
    } catch (error) {
      console.warn('❌ Backend translation failed:', error.message);
    }

    // If translation fails, return original text
    if (!options.silent) {
      console.warn(`⚠️ Translation failed for: "${text}"`);
    }
    return text;
  }

  // Translate an object recursively
  async translateObject(obj, fromLang = 'en', toLang = 'nl') {
    if (typeof obj === 'string') {
      return await this.translateText(obj, fromLang, toLang);
    }

    if (Array.isArray(obj)) {
      const translated = [];
      for (const item of obj) {
        translated.push(await this.translateObject(item, fromLang, toLang));
      }
      return translated;
    }

    if (obj && typeof obj === 'object') {
      const translated = {};
      for (const [key, value] of Object.entries(obj)) {
        translated[key] = await this.translateObject(value, fromLang, toLang);
      }
      return translated;
    }

    return obj;
  }

  // Batch translate multiple texts
  async translateBatch(texts, fromLang = 'en', toLang = 'nl') {
    const promises = texts.map(text => this.translateText(text, fromLang, toLang));
    return await Promise.all(promises);
  }

  // Auto-detect language of text (basic implementation)
  detectLanguage(text) {
    // Simple language detection based on common patterns
    const patterns = {
      nl: /\b(de|het|een|van|in|op|met|voor|door|naar|uit|over|onder|tussen|bij|aan|om|als|maar|en|of|dat|dit|deze|die|zijn|hebben|worden|kunnen|zullen|moeten|mogen|willen)\b/gi,
      en: /\b(the|a|an|of|in|on|with|for|by|to|from|over|under|between|at|as|but|and|or|that|this|these|those|is|are|was|were|have|has|had|will|would|can|could|should|must|may|might)\b/gi,
      fr: /\b(le|la|les|un|une|des|de|du|dans|sur|avec|pour|par|vers|depuis|sous|entre|chez|comme|mais|et|ou|que|ce|cette|ces|ceux|être|avoir|faire|aller|venir|voir|savoir|pouvoir|vouloir|devoir)\b/gi,
      de: /\b(der|die|das|ein|eine|eines|von|in|auf|mit|für|durch|zu|aus|über|unter|zwischen|bei|an|um|als|aber|und|oder|dass|dies|diese|dieser|sein|haben|werden|können|sollen|müssen|mögen|wollen)\b/gi
    };

    let maxMatches = 0;
    let detectedLang = 'en';

    for (const [lang, pattern] of Object.entries(patterns)) {
      const matches = (text.match(pattern) || []).length;
      if (matches > maxMatches) {
        maxMatches = matches;
        detectedLang = lang;
      }
    }

    return detectedLang;
  }

  // Translate content for dynamic pages/dashboard content
  async translateDynamicContent(content, targetLang = 'nl', sourceLang = 'auto') {
    if (!content) return content;

    // Auto-detect source language if needed
    if (sourceLang === 'auto') {
      sourceLang = this.detectLanguage(typeof content === 'string' ? content : JSON.stringify(content));
    }

    // Skip if same language
    if (sourceLang === targetLang) return content;

    try {
      const response = await fetch(`${this.baseURL}/api/translation/translate-object`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          content: content,
          from: sourceLang,
          to: targetLang
        })
      });

      if (!response.ok) {
        throw new Error(`Translation API returned ${response.status}: ${response.statusText}`);
      }

      const data = await response.json();
      return data.translatedContent || content;
    } catch (error) {
      console.warn('Dynamic content translation failed:', error.message);
      
      // Fallback to local translation for simple cases
      if (typeof content === 'string') {
        return await this.translateText(content, sourceLang, targetLang, { silent: true });
      }

      if (Array.isArray(content)) {
        const translated = [];
        for (const item of content) {
          translated.push(await this.translateDynamicContent(item, targetLang, sourceLang));
        }
        return translated;
      }

      if (content && typeof content === 'object') {
        const translated = {};
        for (const [key, value] of Object.entries(content)) {
          // Skip certain fields that shouldn't be translated
          if (['id', 'slug', 'url', 'email', 'phone', 'date', 'timestamp', 'createdAt', 'updatedAt'].includes(key)) {
            translated[key] = value;
          } else {
            translated[key] = await this.translateDynamicContent(value, targetLang, sourceLang);
          }
        }
        return translated;
      }

      return content;
    }
  }

  // Batch translate with rate limiting
  async translateBatchWithRateLimit(texts, fromLang = 'en', toLang = 'nl', delayMs = 100) {
    const results = [];
    for (let i = 0; i < texts.length; i++) {
      const translated = await this.translateText(texts[i], fromLang, toLang, { silent: true });
      results.push(translated);
      
      // Add delay to respect rate limits
      if (i < texts.length - 1) {
        await new Promise(resolve => setTimeout(resolve, delayMs));
      }
    }
    return results;
  }

  // Get supported languages
  getSupportedLanguages() {
    return this.supportedLanguages;
  }

  // Clear all caches
  clearCache() {
    this.cache.clear();
    this.persistentCache.clear();
    localStorage.removeItem('translation-cache');
  }

  // Get cache size
  getCacheSize() {
    return {
      memory: this.cache.size,
      persistent: this.persistentCache.size
    };
  }

  // Auto-detect user's preferred language based on browser/region
  detectUserLanguage() {
    // Check localStorage first
    const savedLang = localStorage.getItem('preferred-language');
    if (savedLang && this.supportedLanguages.includes(savedLang)) {
      return savedLang;
    }

    // Check browser language
    const browserLang = navigator.language || navigator.userLanguage;
    const langCode = browserLang.split('-')[0]; // Get language code without region

    // Map common browser languages to supported languages
    const languageMap = {
      'en': 'en',
      'nl': 'nl',
      'fr': 'fr',
      'de': 'de',
      'es': 'es',
      'it': 'it',
      'pt': 'pt',
      'ru': 'ru',
      'pl': 'pl',
      'sv': 'sv',
      'da': 'da',
      'no': 'no',
      'fi': 'fi',
      'cs': 'cs',
      'sk': 'sk',
      'hu': 'hu',
      'ro': 'ro',
      'bg': 'bg',
      'hr': 'hr',
      'sl': 'sl',
      'et': 'et',
      'lv': 'lv',
      'lt': 'lt'
    };

    return languageMap[langCode] || 'en'; // Default to English
  }

  // Detect user's region for language preferences
  async detectUserRegion() {
    try {
      // Try to get user's location/timezone
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;
      
      // Map timezones to likely language preferences
      const regionLanguageMap = {
        'Europe/Amsterdam': 'nl',
        'Europe/Brussels': 'nl',
        'Europe/Berlin': 'de',
        'Europe/Paris': 'fr',
        'Europe/Madrid': 'es',
        'Europe/Rome': 'it',
        'Europe/Warsaw': 'pl',
        'Europe/Stockholm': 'sv',
        'Europe/Copenhagen': 'da',
        'Europe/Oslo': 'no',
        'Europe/Helsinki': 'fi',
        'Europe/Prague': 'cs',
        'Europe/Bratislava': 'sk',
        'Europe/Budapest': 'hu',
        'Europe/Bucharest': 'ro',
        'Europe/Sofia': 'bg',
        'Europe/Zagreb': 'hr',
        'Europe/Ljubljana': 'sl',
        'Europe/Tallinn': 'et',
        'Europe/Riga': 'lv',
        'Europe/Vilnius': 'lt'
      };

      return regionLanguageMap[timezone] || this.detectUserLanguage();
    } catch (error) {
      console.warn('Region detection failed:', error);
      return this.detectUserLanguage();
    }
  }

  // Set user's preferred language and save it
  setUserLanguage(languageCode) {
    if (this.supportedLanguages.includes(languageCode)) {
      localStorage.setItem('preferred-language', languageCode);
      return true;
    }
    return false;
  }

  // Preload common translations for better performance
  async preloadCommonTranslations(targetLang = 'nl') {
    const commonPhrases = [
      'Loading...',
      'Please wait...',
      'Error occurred',
      'Try again',
      'Success',
      'Failed',
      'Save',
      'Cancel',
      'Delete',
      'Edit',
      'Add',
      'Remove',
      'Search',
      'Filter',
      'Sort',
      'Next',
      'Previous',
      'Submit',
      'Reset',
      'Welcome',
      'Thank you',
      'Contact us',
      'Learn more',
      'Get started',
      'View all',
      'Read more'
    ];

    console.log(`Preloading ${commonPhrases.length} common translations for ${targetLang}...`);
    await this.translateBatchWithRateLimit(commonPhrases, 'en', targetLang, 50);
    console.log('Common translations preloaded successfully');
  }
}

// Create singleton instance
const translationService = new TranslationService();

export default translationService;